import { Facebook, Twitter, Radio, MapPin, Clock } from "lucide-react";

const ACCENT = {
  tornado: "#d90429",
  severe: "#ffd60a",
  flood: "#2a9d8f",
  winter: "#4cc9f0",
  spc: "#f77f00",
  default: "#d90429",
};

function accentFor(cutIn) {
  const t = (cutIn.title || "").toLowerCase();
  if (cutIn.kind === "spc") return ACCENT.spc;
  if (t.includes("tornado")) return ACCENT.tornado;
  if (t.includes("thunderstorm")) return ACCENT.severe;
  if (t.includes("flood")) return ACCENT.flood;
  if (t.includes("winter") || t.includes("ice") || t.includes("snow") || t.includes("freeze")) return ACCENT.winter;
  return ACCENT.default;
}

function fmtExpires(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "America/New_York" }) + " ET";
}

function safetyFor(cutIn) {
  const t = (cutIn.title || "").toLowerCase();
  if (t.includes("tornado")) return "Take cover now — lowest floor, interior room, away from windows.";
  if (t.includes("flash flood") || t.includes("flood")) return "Turn around, don't drown. Never drive through flooded roads.";
  if (t.includes("thunderstorm")) return "Move indoors away from windows. Expect damaging winds and hail.";
  if (t.includes("hurricane") || t.includes("tropical")) return "Finish preparations now and follow local evacuation orders.";
  if (t.includes("extreme wind")) return "Treat this like a tornado — shelter in an interior room immediately.";
  if (cutIn.kind === "spc") return "Severe storms are possible today. Have a way to receive warnings.";
  return "Stay weather aware and keep watching for updates.";
}

const STATUS_LABEL = { posted: "Posted", failed: "Failed", test: "Test", pending: "Sending…" };

function PlatformChip({ icon: Icon, label, status, testId }) {
  const st = status || "pending";
  const color = st === "posted" ? "#22c55e" : st === "failed" ? "#f87171" : st === "test" ? "#facc15" : "#9ca3af";
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-2 rounded-[4px] bg-[#eef2f7] px-4 py-2"
    >
      <Icon className="h-5 w-5 text-[#0a0f1c]" />
      <span className="font-oswald text-[18px] font-bold uppercase tracking-widest text-[#0a0f1c]">{label}</span>
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
      <span className="font-oswald text-[16px] font-bold uppercase tracking-widest" style={{ color }}>{STATUS_LABEL[st] || st}</span>
    </div>
  );
}

export function CutInSlide({ cutIn, now }) {
  const accent = accentFor(cutIn);
  const total = Math.max(1, cutIn.dwellMs || 60000);
  const remaining = Math.max(0, (cutIn.endsAt || 0) - (now?.getTime?.() ?? Date.now()));
  const pct = Math.min(100, Math.max(0, (remaining / total) * 100));
  const until = fmtExpires(cutIn.expires);
  const counties = (cutIn.area || "").split(";").map((c) => c.trim()).filter(Boolean);
  const dark = accent === "#ffd60a" || accent === "#4cc9f0" || accent === "#f77f00";
  const ink = dark ? "#0a0f1c" : "#fff";

  return (
    <div data-testid="broadcast-cutin-slide" className="relative h-full w-full overflow-hidden text-white" style={{ background: "linear-gradient(135deg, #f4f6fa 0%, #dfe6ef 100%)" }}>
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #000 0 2px, transparent 2px 26px)" }} />
      {/* Accent panel sweeps in from the left */}
      <div className="alert-wipe absolute inset-y-0 left-0 w-[52%]" style={{ background: `linear-gradient(160deg, ${accent} 0%, ${accent}cc 100%)`, clipPath: "polygon(0 0, 100% 0, 90% 100%, 0 100%)" }} />
      <div className="alert-wipe absolute inset-y-0 left-[47%] w-[3%] bg-[#0a0f1c]" style={{ clipPath: "polygon(60% 0, 100% 0, 40% 100%, 0 100%)", animationDelay: "120ms" }} />

      {/* Breaking bar */}
      <div className="alert-wipe absolute inset-x-0 top-0 flex h-[64px] items-center gap-6 bg-[#0a0f1c] px-[60px]" style={{ animationDelay: "80ms" }}>
        <span className="alert-stripes flex h-full items-center px-6 font-oswald text-[28px] font-black uppercase tracking-[0.35em]" style={{ background: accent, color: ink }}>Breaking</span>
        <span className="flex items-center gap-3 font-oswald text-[26px] font-bold uppercase tracking-[0.3em]"><Radio className="alert-pulse h-6 w-6" style={{ color: accent }} /> {cutIn.kind === "spc" ? "Storm Prediction Center" : "National Weather Service"} · Weather alert</span>
        <span className="ml-auto font-oswald text-[22px] font-bold uppercase tracking-[0.3em] text-white/70">Georgia Weather Network</span>
      </div>

      {/* Headline */}
      <div className="absolute left-[70px] top-[120px] w-[780px]" style={{ color: ink }}>
        <div className="cutin-rise inline-flex items-center gap-3 rounded-full bg-black/30 px-5 py-2 font-oswald text-[22px] font-bold uppercase tracking-[0.3em] text-white" style={{ animationDelay: "250ms" }}>
          <span className="live-dot" style={{ width: 12, height: 12 }} /> {cutIn.kind === "spc" ? "Severe outlook" : "Warning issued"}
        </div>
        <div className="cutin-rise mt-6 font-oswald text-[112px] font-black uppercase leading-[0.9] tracking-wide drop-shadow-[0_10px_30px_rgba(0,0,0,0.3)]" style={{ animationDelay: "350ms" }} data-testid="cutin-title">{cutIn.title}</div>
        {until && (
          <div className="cutin-rise mt-7 inline-flex items-center gap-3 rounded-[4px] bg-[#0a0f1c] px-5 py-2 font-oswald text-[30px] font-bold uppercase tracking-wider text-white" style={{ animationDelay: "500ms" }} data-testid="cutin-expires">
            <Clock className="h-7 w-7" style={{ color: "#ffd60a" }} /> In effect until {until}
          </div>
        )}
        {counties.length > 0 && (
          <div className="cutin-rise mt-6" style={{ animationDelay: "600ms" }} data-testid="cutin-area">
            <div className="flex items-center gap-2 font-oswald text-[20px] font-bold uppercase tracking-[0.3em] opacity-80"><MapPin className="h-5 w-5" /> Counties ({counties.length})</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {counties.slice(0, 12).map((c) => <span key={c} className="rounded-[3px] bg-white px-3 py-1 font-oswald text-[22px] font-bold uppercase tracking-wide text-[#0a0f1c]">{c.replace(/,\s*[A-Z]{2}$/, "")}</span>)}
              {counties.length > 12 && <span className="rounded-[3px] bg-white/70 px-3 py-1 font-oswald text-[22px] font-bold uppercase text-[#0a0f1c]">+{counties.length - 12} more</span>}
            </div>
          </div>
        )}
      </div>

      {/* Graphic card */}
      <div className="cutin-pop absolute right-[60px] top-[110px] w-[1000px] overflow-hidden rounded-[6px] bg-white shadow-[0_30px_80px_rgba(10,15,28,0.35)]" style={{ animationDelay: "300ms" }}>
        <div className="flex items-center justify-between px-6 py-3 font-oswald text-[22px] font-black uppercase tracking-[0.25em] text-white" style={{ background: "#0a0f1c" }}>
          <span>Warning area</span>
          <span className="flex items-center gap-2 text-[18px] font-bold tracking-widest text-white/70"><span className="live-dot" /> Same graphic now on social</span>
        </div>
        <div className="relative aspect-[16/9] w-full bg-[#cfd3d8]">
          {cutIn.image_url ? (
            <img data-testid="cutin-image" src={cutIn.image_url} alt={cutIn.title} className="h-full w-full object-contain" />
          ) : (
            <div className="flex h-full w-full items-center justify-center px-10 text-center font-oswald text-[48px] font-black uppercase leading-tight text-[#0a0f1c]/60">{cutIn.title}</div>
          )}
        </div>
        <div className="flex items-center gap-3 px-6 py-4">
          <PlatformChip icon={Facebook} label="Facebook" status={cutIn.platforms?.facebook} testId="cutin-chip-facebook" />
          <PlatformChip icon={Twitter} label="X" status={cutIn.platforms?.twitter} testId="cutin-chip-twitter" />
          <span className="ml-auto font-oswald text-[20px] font-bold uppercase tracking-[0.25em] text-[#5b6675]" data-testid="cutin-countdown">Resuming loop · {Math.ceil(remaining / 1000)}s</span>
        </div>
      </div>

      {/* Safety call-to-action */}
      <div className="cutin-rise absolute bottom-[60px] left-[70px] w-[760px] rounded-[6px] bg-[#0a0f1c] px-7 py-5 text-white" style={{ animationDelay: "750ms" }} data-testid="cutin-safety">
        <div className="font-oswald text-[20px] font-bold uppercase tracking-[0.3em]" style={{ color: "#ffd60a" }}>What to do now</div>
        <div className="mt-1 font-oswald text-[34px] font-black uppercase leading-tight">{safetyFor(cutIn)}</div>
      </div>

      {/* Countdown bar */}
      <div className="absolute inset-x-0 bottom-0 h-[12px] bg-[#0a0f1c]/20">
        <div className="h-full transition-[width] duration-1000 ease-linear" style={{ width: `${pct}%`, background: accent }} />
      </div>
    </div>
  );
}
